import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { AuthService } from 'src/app/core/services/auth.service';
import {
  InappNotificationsService,
  InappNotificationRow,
  InappItemType,
} from 'src/app/core/services/inapp-notifications.service';
import { resolveInappNotificationTarget } from 'src/app/core/services/inapp-notification-target-rules';

@Component({
  selector: 'app-inapp-notification-bell',
  standalone: true,
  imports: [CommonModule, MaterialModule, TablerIconsModule],
  templateUrl: './inapp-notification-bell.component.html',
  styleUrls: ['./inapp-notification-bell.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InappNotificationBellComponent implements OnInit, OnDestroy {
  @Input() itemType: InappItemType = 'notification';

  unread = 0;
  items: InappNotificationRow[] = [];
  menuOpen = false;

  private readonly api = inject(InappNotificationsService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  private badgeSub: Subscription | null = null;
  private lastBadgeCount = 0;

  get isTaskBell(): boolean {
    return this.itemType === 'task';
  }

  get menuTitle(): string {
    return this.isTaskBell ? 'Tasks to do' : 'Notifications';
  }

  get emptyLabel(): string {
    return this.isTaskBell ? 'No tasks' : 'No notifications';
  }

  get ariaLabel(): string {
    return this.isTaskBell ? 'Tasks to do' : 'Notifications';
  }

  get unreadBadgeText(): string {
    if (this.unread <= 0) {
      return '';
    }
    return this.unread === 1 ? '1 new' : `${this.unread} new`;
  }

  rowTablerIcon(row: InappNotificationRow): string {
    if (this.isTaskBell) {
      if (row.category === 'payment') {
        return 'building-bank';
      }
      return 'clipboard-check';
    }
    if (row.category === 'billing') {
      return 'receipt';
    }
    if (row.category === 'payment') {
      return 'building-bank';
    }
    return 'bell';
  }

  rowIconClasses(row: InappNotificationRow): Record<string, boolean> {
    if (this.isTaskBell) {
      const pending = this.rowPendingTask(row);
      return {
        'text-warning': pending,
        'bg-light-warning': pending,
        'text-success': !pending,
        'bg-light-success': !pending,
      };
    }
    const read = this.rowNotificationRead(row);
    return {
      'text-primary': !read,
      'bg-light-primary': !read,
      'text-muted': read,
      'bg-light-secondary': read,
    };
  }

  ngOnInit(): void {
    if (!this.auth.isLogged()) {
      return;
    }

    this.badgeSub = this.api.badgeCount$(this.itemType).subscribe((count) => {
      if (this.menuOpen && this.isTaskBell && count < this.lastBadgeCount) {
        this.loadItems();
      }
      this.lastBadgeCount = count;
      this.unread = count;
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.badgeSub?.unsubscribe();
    this.badgeSub = null;
  }

  onMenuOpened(): void {
    this.menuOpen = true;
    // Tasks: badge tracks pending (is_done = 0), so opening must NOT clear it.
    // Notifications keep "mark read on close" semantics.
    this.loadItems();
  }

  onMenuClosed(): void {
    this.menuOpen = false;

    if (!this.isTaskBell) {
      this.api.markAllRead('notification').subscribe({
        next: () => {
          this.items = this.items.map((i) => ({ ...i, is_read: 1 }));
          this.unread = 0;
          this.api.setBadgeCount('notification', 0);
          this.cdr.detectChanges();
        },
        error: () => this.api.refreshBadgeCount('notification'),
      });
      return;
    }

    this.api.refreshBadgeCount('task');
  }

  private loadItems(): void {
    this.api.list(15, 0, this.itemType).subscribe({
      next: (res) => {
        if (res?.status === 'success' && Array.isArray(res.data)) {
          this.items = this.isTaskBell
            ? res.data.filter((row) => this.rowPendingTask(row))
            : res.data;
          this.cdr.detectChanges();
        }
      },
      error: () => {},
    });
  }

  open(row: InappNotificationRow): void {
    if (!this.isTaskBell && !this.rowNotificationRead(row)) {
      this.markNotificationRead(row);
    }
    // Target resolved from the row, not from its stored link: a task that asks for an action
    // deep-links to it (see inapp-notification-target-rules).
    const target = resolveInappNotificationTarget(row);
    if (target) {
      void this.router.navigateByUrl(target);
    }
  }

  private markNotificationRead(row: InappNotificationRow): void {
    const id = Number(row.id);
    if (!Number.isFinite(id) || id < 1) {
      return;
    }
    row.is_read = 1;
    this.api.adjustBadgeCount('notification', -1);
    this.api.markRead(id).subscribe({
      error: () => {
        row.is_read = 0;
        this.api.adjustBadgeCount('notification', 1);
      },
    });
  }

  markAsDone(row: InappNotificationRow, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.rowPendingTask(row)) {
      return;
    }
    this.api.adjustBadgeCount('task', -1);
    this.api.markDone(Number(row.id)).subscribe({
      next: () => {
        this.items = this.items.filter((item) => Number(item.id) !== Number(row.id));
        this.cdr.detectChanges();
        this.api.refreshBadgeCount('task');
      },
      error: () => this.api.refreshBadgeCount('task'),
    });
  }

  markAllDone(): void {
    this.api.markAllDone().subscribe({
      next: () => {
        this.items = [];
        this.api.setBadgeCount('task', 0);
        this.cdr.detectChanges();
      },
      error: () => this.api.refreshBadgeCount('task'),
    });
  }

  formatWhen(iso: string): string {
    if (!iso) {
      return '';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    return d.toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  }

  rowNotificationRead(row: InappNotificationRow): boolean {
    return Number(row.is_read) === 1;
  }

  rowPendingTask(row: InappNotificationRow): boolean {
    return this.isTaskBell && Number(row.is_done) !== 1;
  }
}
